import { useCallback, useState } from 'react';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { useRevitApi } from '../../../shared/api/revit/useRevitApi';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import { webLog } from '../../../shared/api/grpc/webLogger';
import { useBatchOperation } from '../../../shared/lib/batchOperations';
import { normalizeReconnectionResult, type ReconnectionResultState } from '../lib/validationReconnectNormalizer';
import {
  buildReconnectAllUnresolvedDetail,
  buildReconnectAllUnresolvedToast,
} from '../lib/validationReconnectCopy';
import type { ValidationRunCoordinator } from '../lib/validationRunCoordinator';
import type { RuntimeCapabilities } from '../../../shared/api/runtime/capabilities';
import type { PcfValidationReport } from '../types';

export interface UseValidationReconnectOptions {
  runtimeCapabilities: RuntimeCapabilities;
  coordinator?: ValidationRunCoordinator<PcfValidationReport>;
  runFocusedValidation: (validationIds: number[]) => Promise<void>;
}

export interface UseValidationReconnectReturn {
  reconnecting: boolean;
  reconnectionResult: ReconnectionResultState | null;
  reconnectDialogOpen: boolean;
  handleReconnectPair: (elementId1: string, elementId2: string) => Promise<void>;
  handleReconnectAll: (validationId: number) => Promise<void>;
  closeReconnectDialog: () => void;
}

export function useValidationReconnect(
  options: UseValidationReconnectOptions
): UseValidationReconnectReturn {
  const { runtimeCapabilities, coordinator, runFocusedValidation } = options;
  const { call } = useRevitApi();
  const snackbar = useSnackbarStore();
  const reconnectBatchOperation = useBatchOperation();

  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectionResult, setReconnectionResult] = useState<ReconnectionResultState | null>(null);
  const [reconnectDialogOpen, setReconnectDialogOpen] = useState(false);

  const handleReconnectPair = useCallback(
    async (elementId1: string, elementId2: string) => {
      if (!runtimeCapabilities.canReconnect) {
        snackbar.showInfo(
          `Reconnect pair is not available in ${runtimeCapabilities.mode} mode (${elementId1}, ${elementId2})`
        );
        return;
      }

      try {
        setReconnecting(true);
        const startedAt = new Date().toISOString();
        setReconnectDialogOpen(true);
        setReconnectionResult({
          status: 'queued',
          operationKind: 'single',
          operationLabel: 'Reconnect pair',
          phaseLabel: 'Queued',
          detail: `Reconnect pair ${elementId1}-${elementId2} was queued for Revit.`,
          startedAt,
          refreshState: 'pending',
          refreshMessage: 'Waiting for the Revit connector to report progress.',
          processed: 0,
          totalPairs: 1,
          successfulReconnections: undefined,
          failedReconnections: undefined,
          successMessages: [],
          errorMessages: [],
        });
        const result = await call('validation.reconnectElements', {
          elementId1,
          elementId2,
          onProgress: (progress: RevitOperationProgressSnapshot) => {
            const waiting =
              progress.state === 'waiting_for_revit' || progress.state === 'waiting_for_user_input';
            setReconnectionResult((current: any) => ({
              ...(current || {}),
              status: waiting ? 'running' : progress.failed ? 'error' : 'running',
              operationKind: 'single',
              operationLabel: 'Reconnect pair',
              phaseLabel: progress.message || (waiting ? 'Waiting for Revit' : 'Running in Revit'),
              detail: progress.waitReason || progress.currentItem || progress.detail || current?.detail,
              processed: progress.processed ?? current?.processed,
              totalPairs: progress.total ?? current?.totalPairs,
              percent: waiting ? null : progress.percent ?? current?.percent,
              elapsedMs: progress.elapsedMs ?? current?.elapsedMs,
              etaMs: waiting ? null : progress.etaMs ?? current?.etaMs,
              waitReason: progress.waitReason ?? current?.waitReason,
              waitElapsedMs: progress.waitElapsedMs ?? current?.waitElapsedMs,
              activeElapsedMs: progress.activeElapsedMs ?? current?.activeElapsedMs,
              operationState: progress.state,
              operationId: progress.operationId,
            }));
          },
        });

        if (result.success) {
          coordinator?.invalidateReports();
          setReconnectionResult((current: any) => ({
            ...(current || {}),
            status: 'completed',
            operationKind: 'single',
            operationLabel: 'Reconnect pair',
            phaseLabel: 'Complete',
            detail: result.message || `Reconnected ${elementId1}-${elementId2}.`,
            completedAt: new Date().toISOString(),
            refreshState: 'completed',
            refreshMessage: 'Reconnect pair finished.',
            attemptedPairCount: 1,
            totalPairs: 1,
            successfulReconnections: 1,
            failedReconnections: 0,
            processed: 1,
            percent: current?.percent,
          }));
          snackbar.showSuccess(result.message);
        } else {
          setReconnectionResult((current: any) => ({
            ...(current || {}),
            status: 'error',
            operationKind: 'single',
            operationLabel: 'Reconnect pair',
            phaseLabel: 'Failed',
            detail: result.error || 'Reconnection failed.',
            completedAt: new Date().toISOString(),
            refreshState: 'failed',
            refreshMessage: result.error || 'Reconnect pair failed.',
            attemptedPairCount: 1,
            totalPairs: 1,
            successfulReconnections: 0,
            failedReconnections: 1,
          }));
          snackbar.showError(result.error || 'Reconnection failed');
        }
      } catch (error: any) {
        setReconnectionResult((current: any) => ({
          ...(current || {}),
          status: 'error',
          operationKind: 'single',
          operationLabel: 'Reconnect pair',
          phaseLabel: 'Failed',
          detail: error.message || 'Reconnection error',
          completedAt: new Date().toISOString(),
          refreshState: 'failed',
          refreshMessage: error.message || 'Reconnect pair failed.',
          attemptedPairCount: 1,
          totalPairs: 1,
          successfulReconnections: 0,
          failedReconnections: 1,
        }));
        snackbar.showError(error.message || 'Reconnection error');
      } finally {
        setReconnecting(false);
      }
    },
    [call, coordinator, runtimeCapabilities.canReconnect, runtimeCapabilities.mode, snackbar]
  );

  const handleReconnectAll = useCallback(
    async (validationId: number) => {
      if (!runtimeCapabilities.canReconnect) {
        snackbar.showInfo(
          `Reconnect all is not available in ${runtimeCapabilities.mode} mode (Validation ${validationId})`
        );
        return;
      }

      try {
        setReconnecting(true);
        const startedAt = new Date().toISOString();
        setReconnectDialogOpen(true);
        setReconnectionResult({
          status: 'queued',
          operationKind: 'all',
          operationLabel: 'Reconnect all',
          phaseLabel: 'Queued',
          detail: `Reconnect all was queued for validation #${validationId}. Waiting for Revit to start processing.`,
          startedAt,
          validationId,
          refreshState: 'pending',
          refreshMessage: 'Scoped validation result will be updated from the host reconnect summary.',
          totalPairs: undefined,
          successfulReconnections: undefined,
          failedReconnections: undefined,
          successMessages: [],
          errorMessages: [],
        });
        webLog.info(
          'validation-reconnect',
          `reconnect-all:start validationId=${validationId} mode=${runtimeCapabilities.mode}`
        );

        const queueResult = await call('validation.reconnectAllPairs', {
          validationId,
          onProgress: (progress: RevitOperationProgressSnapshot) => {
            const waiting =
              progress.state === 'waiting_for_revit' || progress.state === 'waiting_for_user_input';
            setReconnectionResult((current: any) => ({
              ...(current || {}),
              status: waiting ? 'running' : progress.failed ? 'error' : 'running',
              operationKind: 'all',
              operationLabel: 'Reconnect all',
              phaseLabel: progress.message || (waiting ? 'Waiting for Revit' : 'Running in Revit'),
              detail: progress.waitReason || progress.currentItem || progress.detail || current?.detail,
              processed: progress.processed ?? current?.processed,
              totalPairs: progress.total ?? current?.totalPairs,
              percent: waiting ? null : progress.percent ?? current?.percent,
              elapsedMs: progress.elapsedMs ?? current?.elapsedMs,
              etaMs: waiting ? null : progress.etaMs ?? current?.etaMs,
              waitReason: progress.waitReason ?? current?.waitReason,
              waitElapsedMs: progress.waitElapsedMs ?? current?.waitElapsedMs,
              activeElapsedMs: progress.activeElapsedMs ?? current?.activeElapsedMs,
              operationState: progress.state,
              operationId: progress.operationId,
            }));
          },
        });

        if (!queueResult.success) {
          let parsedFailedResult: any = null;
          try {
            const failedResult = await call('validation.getReconnectionResult', {});
            parsedFailedResult =
              (failedResult.data ? normalizeReconnectionResult(failedResult.data) : null) ??
              (typeof failedResult.resultJson === 'string' && failedResult.resultJson
                ? normalizeReconnectionResult(JSON.parse(failedResult.resultJson))
                : null);
          } catch {
            parsedFailedResult = null;
          }

          webLog.error(
            'validation-reconnect',
            `reconnect-all:queue-failed validationId=${validationId} error=${queueResult.error || 'unknown'}`
          );

          if (parsedFailedResult) {
            if ((parsedFailedResult.successfulReconnections ?? 0) > 0) {
              await runFocusedValidation([validationId]);
            }
            const completedAt = new Date().toISOString();
            setReconnectionResult({
              ...parsedFailedResult,
              status: 'error',
              operationKind: 'all',
              operationLabel: 'Reconnect all',
              phaseLabel: 'Validation still failing',
              detail: queueResult.error || buildReconnectAllUnresolvedToast(validationId),
              startedAt,
              completedAt,
              validationId,
              refreshState: 'completed',
              refreshMessage:
                (parsedFailedResult.successfulReconnections ?? 0) > 0
                  ? `Validation #${validationId} was refreshed and merged into the current report.`
                  : 'Focused validation was skipped because reconnect all changed no pairs.',
            });
            snackbar.showError(queueResult.error || buildReconnectAllUnresolvedToast(validationId));
          } else {
            snackbar.showError(`Failed to start: ${queueResult.error}`);
            setReconnectionResult((current: any) => ({
              ...(current || {}),
              status: 'error',
              phaseLabel: 'Failed to start',
              detail: queueResult.error || 'Failed to queue reconnect all.',
              completedAt: new Date().toISOString(),
              refreshState: 'failed',
              refreshMessage: 'Validation refresh was skipped because reconnect all never started.',
            }));
          }
          setReconnecting(false);
          return;
        }

        setReconnectionResult((current: any) => ({
          ...(current || {}),
          status: 'running',
          phaseLabel: 'Running in Revit',
          detail: `Reconnect all is running for validation #${validationId}. This dialog will update when Revit returns the summary.`,
        }));
        webLog.info('validation-reconnect', `reconnect-all:queued validationId=${validationId}`);

        try {
          const { result, attempts } = await reconnectBatchOperation.run({
            title: 'Reconnect all',
            message: `Reconnect all is running for validation #${validationId}.`,
            run: async (helpers) => {
              helpers.updatePhase('polling', 'Waiting for Revit to return the reconnect summary.');
              return helpers.pollUntil({
                intervalMs: 1000,
                initialDelayMs: 0,
                maxAttempts: 60,
                poll: async (attempt) => {
                  const result = await call('validation.getReconnectionResult', {});
                  webLog.info(
                    'validation-reconnect',
                    `reconnect-all:poll validationId=${validationId} attempt=${attempt} status=${result.status ?? 'unknown'} success=${String(result.success)}`
                  );

                  if (result.status === 'pending') {
                    setReconnectionResult((current: any) => ({
                      ...(current || {}),
                      status: 'running',
                      phaseLabel: 'Running in Revit',
                      detail: 'Reconnect all is still processing. Waiting for Revit to return the summary.',
                      pollingAttempt: attempt,
                    }));
                  }

                  return result;
                },
                isPending: (result) => result.status === 'pending',
                timeoutMessage: 'Timeout: Reconnection took too long',
              });
            },
            toSummary: ({ result }: any) => {
              const parsedResult =
                (result.data ? normalizeReconnectionResult(result.data) : null) ??
                (typeof result.resultJson === 'string' && result.resultJson
                  ? normalizeReconnectionResult(JSON.parse(result.resultJson))
                  : null);

              return {
                total: parsedResult?.totalPairs ?? 0,
                succeeded: parsedResult?.successfulReconnections ?? 0,
                failed: parsedResult?.failedReconnections ?? (result.success ? 0 : 1),
              };
            },
          });

          const parsedResult =
            (result.data ? normalizeReconnectionResult(result.data) : null) ??
            (typeof result.resultJson === 'string' && result.resultJson
              ? normalizeReconnectionResult(JSON.parse(result.resultJson))
              : null);

          if (parsedResult) {
            const completedAt = new Date().toISOString();
            if ((parsedResult.successfulReconnections ?? 0) > 0) {
              await runFocusedValidation([validationId]);
            }

            if (result.success) {
              webLog.info(
                'validation-reconnect',
                `reconnect-all:completed validationId=${validationId} totalPairs=${parsedResult.totalPairs ?? 'n/a'} successful=${parsedResult.successfulReconnections ?? 'n/a'} failed=${parsedResult.failedReconnections ?? 'n/a'}`
              );
              setReconnectionResult({
                ...parsedResult,
                status: 'completed',
                operationKind: 'all',
                operationLabel: 'Reconnect all',
                phaseLabel: 'Completed',
                detail: 'Reconnect all finished and refreshed the affected validation.',
                startedAt,
                completedAt,
                validationId,
                pollingAttempt: attempts,
                refreshState: 'completed',
                refreshMessage: `Validation #${validationId} was refreshed and merged into the current report.`,
              });
            } else {
              webLog.error(
                'validation-reconnect',
                `reconnect-all:completed-with-unresolved-validation validationId=${validationId} totalPairs=${parsedResult.totalPairs ?? 'n/a'} successful=${parsedResult.successfulReconnections ?? 'n/a'} failed=${parsedResult.failedReconnections ?? 'n/a'} error=${result.error || 'unknown'}`
              );
              setReconnectionResult({
                ...parsedResult,
                status: 'error',
                operationKind: 'all',
                operationLabel: 'Reconnect all',
                phaseLabel: 'Validation still failing',
                detail: buildReconnectAllUnresolvedDetail(validationId),
                startedAt,
                completedAt,
                validationId,
                pollingAttempt: attempts,
                refreshState: 'completed',
                refreshMessage:
                  (parsedResult.successfulReconnections ?? 0) > 0
                    ? `Validation #${validationId} was refreshed and merged into the current report.`
                    : 'Focused validation was skipped because reconnect all changed no pairs.',
              });
            }

            if (result.success) {
              snackbar.showSuccess(
                `Reconnect all finished: ${parsedResult.successfulReconnections ?? 0}/${parsedResult.totalPairs ?? 0} pairs reconnected.`
              );
            } else {
              snackbar.showError(result.error || buildReconnectAllUnresolvedToast(validationId));
            }
          } else {
            webLog.error(
              'validation-reconnect',
              `reconnect-all:result-failed validationId=${validationId} error=${result.error || 'unknown'}`
            );
            snackbar.showError(result.error || 'Reconnection failed');
            setReconnectionResult((current: any) => ({
              ...(current || {}),
              status: 'error',
              phaseLabel: 'Failed',
              detail: result.error || 'Reconnect all failed.',
              completedAt: new Date().toISOString(),
              refreshState: 'failed',
              refreshMessage: 'Validation refresh was skipped because reconnect all failed.',
            }));
          }
        } catch (error: any) {
          webLog.error(
            'validation-reconnect',
            `reconnect-all:poll-exception validationId=${validationId} attempt=${reconnectBatchOperation.state.attempt ?? 0} error=${error.message || 'unknown'}`
          );
          snackbar.showError(error.message || 'Reconnection error');
          setReconnectionResult((current: any) => ({
            ...(current || {}),
            status: 'error',
            phaseLabel: 'Failed',
            detail: error.message || 'Reconnect all failed.',
            completedAt: new Date().toISOString(),
            refreshState: 'failed',
            refreshMessage: 'Validation refresh was skipped because reconnect all failed.',
          }));
        } finally {
          setReconnecting(false);
        }
      } catch (error: any) {
        webLog.error(
          'validation-reconnect',
          `reconnect-all:exception validationId=${validationId} error=${error.message || 'unknown'}`
        );
        snackbar.showError(error.message || 'Reconnection error');
        setReconnecting(false);
      }
    },
    [call, reconnectBatchOperation, runFocusedValidation, runtimeCapabilities.canReconnect, runtimeCapabilities.mode, snackbar]
  );

  const closeReconnectDialog = useCallback(() => {
    setReconnectDialogOpen(false);
    setReconnectionResult(null);
  }, []);

  return {
    reconnecting,
    reconnectionResult,
    reconnectDialogOpen,
    handleReconnectPair,
    handleReconnectAll,
    closeReconnectDialog,
  };
}
